import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import dynamicLanguage from '@/commons/components/monaco-editor/iqp-dsl-language/dynamicLanguage.js'
import './monaco.scss'

// Это нужно сделать, что-бы groovy появился в markdown
monaco.editor.createModel('', 'groovy').dispose()

function noop() {
}

let _nextElId = 0;
function nextElId() {
    return _nextElId++ + '';
}

let _nextId = 0;
function nextId() {
    return _nextId++;
}

export {monaco};

export default {
    name: 'MonacoEditor',
    props: {
        diffEditor: {type: Boolean, default: false},
        width: {type: [String, Number], default: '100%'},
        height: {type: [String, Number], default: '100%'},
        original: String,
        value: String,
        language: {type: String, default: 'groovy'},
        path: {type: String},
        theme: {type: String, default: 'vs'},
        options: {
            type: Object,
            default() {
                // https://microsoft.github.io/monaco-editor/api/modules/monaco.editor.html#EditorOptions
                return {
                    autoIndent: true,
                    formatOnPast: true,
                    formatOnType: true,
                    // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ieditoroptions.html
                    automaticLayout: true,
                    fontSize: 11,
                    scrollbar: {
                      // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ieditorscrollbaroptions.html
                        verticalScrollbarSize: 6,
                        horizontalScrollbarSize: 6
                    },
                    // Configuration options for editor hover
                    // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.IEditorHoverOptions.html
                    hover: {
                        enabled: true, // Enable the hover
                        above: true,   // Should the hover be shown above the line if possible?
                        delay: 300,    // Delay for showing the hover
                        hidingDelay: 500, // Controls how long the hover is visible after you hovered out of it. Require sticky setting to be true.
                        sticky: true,  // Is the hover sticky such that it can be clicked and its contents selected?
                    }
                };
            }
        },
        editorMounted: {type: Function, default: noop},
        editorBeforeMount: {type: Function, default: noop}
    },

    watch: {
        options: {
            deep: true,
            handler(options) {
                this.editor && this.editor.updateOptions(options);
            }
        },

        value() {
            this.editor && this.value !== this._getValue() && this._setValue(this.value);
        },

        language() {
            if (!this.editor) return;
            const lang = dynamicLanguage(this.language);
            if (this.diffEditor) {
                const {original, modified} = this.editor.getModel();
                monaco.editor.setModelLanguage(original, lang.id);
                monaco.editor.setModelLanguage(modified, lang.id);
            } else {
                monaco.editor.setModelLanguage(this.editor.getModel(), lang.id);
            }
        },
        path() {
            if (!this.editor) return;
            this._reinitModel();
        },
        theme() {
            this.editor && monaco.editor.setTheme(this.theme);
        },

        style() {
            this.editor && this.$nextTick(() => {
                this.editor.layout();
            });
        },
    },

    computed: {
        style() {
            return {
                width: !/^\d+$/.test(this.width) ? this.width : `${this.width}px`,
                height: !/^\d+$/.test(this.height) ? `calc(${this.height} - 28px)` : `${this.height}px`
            }
        }
    },

    beforeCreate() {
        this.elId = nextElId();
    },

    mounted() {
        this.initMonaco();
    },

    beforeDestroy() {
        this.editor && this.editor.dispose();
        delete this.editor;
    },

    render(h) {
        return h('div', {class:'fst-monaco-editor-container', style: this.style, attrs: {id: 'MonacoEditor_' + this.elId}});
    },

    methods: {
        initMonaco() {
            this.editor && this.editor.dispose();
            delete this.editor;

            const {value, language, theme, options} = this;
            Object.assign(options, this._editorBeforeMount());

            if (this.diffEditor) {
                this.editor = monaco.editor.createDiffEditor(this.$el, {
                    model: {
                        original: this._createModel(this.original),
                        modified: this._createModel(this.value)
                    },
                    theme: theme,
                    ...options
                });
            } else {
                this.editor = monaco.editor.create(this.$el, {
                    model: this._createModel(this.value),
                    theme: theme,
                    ...options
                });
            }
            this._editorMounted(this.editor);
        },

        _createUri() {
            const path = this.path, language = this.language;
            if(path && path.length) {
                return monaco.Uri.parse(`inmemory:/${path}/${nextId()}.${language}`);
            }
            return monaco.Uri.parse(`inmemory://model/${nextId()}.${language}`);
        },

        _createModel(value) {
            const lang = dynamicLanguage(this.language);
            return monaco.editor.createModel(value, lang.id, this._createUri());
        },

        _reinitModel() {
            if(!this.editor) {
                return;
            }0
            if (this.diffEditor) {
                const {original, modified} = this.editor.getModel();
                this.editor.setModel({
                    original: this._createModel(original.getValue()),
                    modified: this._createModel(modified.getValue())
                });
                original.dispose();
                modified.dispose();
            } else {
                const model = this.editor.getModel();
                this.editor.setModel(this._createModel(model.getValue()));
                model.dispose();
            }
        },

        _getEditor() {
            if (!this.editor) return null;
            return this.diffEditor ? this.editor.modifiedEditor : this.editor;
        },

        _setValue(value) {
            let editor = this._getEditor();
            if (editor) return editor.setValue(value);
        },

        _getValue() {
            let editor = this._getEditor();
            if (!editor) return '';
            return editor.getValue();
        },

        _editorBeforeMount() {
            const options = this.editorBeforeMount(monaco);
            return options || {};
        },

        _editorMounted(editor) {
            this.editorMounted(editor, monaco);
            if (this.diffEditor) {
                editor.onDidUpdateDiff((event) => {
                    const value = this._getValue();
                    this._emitChange(value, event);
                });
            } else {
                editor.onDidChangeModelContent(event => {
                    const value = this._getValue();
                    this._emitChange(value, event);
                });
            }
        },

        _emitChange(value, event) {
            this.$emit('change', value, event);
            this.$emit('input', value);
        }
    }
}
